import { useCallback, useEffect, useRef, useState } from "react";
import {
  classifyUpload,
  fileBaseName,
  fileToPdfBuffer,
} from "./file-to-pdf-buffer";
import { rotatePdfPages } from "./rotate-pdf-pages";
import { documentFileName, downloadSource } from "./download-source";
import {
  COMPRESS_MAX_BYTES,
  CompressEntry,
  MERGED_COMPRESS_ID,
  canCompressEntry,
  canDownloadEntry,
  formatByteSize,
  pdfBytesFromCompressResponse,
  pdfBytesToFile,
  toCompressDocuments,
} from "./compress-documents";
import type {
  PdfCompressDownload,
  PdfCompressionErrorContext,
  PdfCompressionOrigin,
  PdfMergeDocument,
  PdfMergePanelProps,
} from "./types";

export interface UsePdfCompressOptions
  extends Pick<
    PdfMergePanelProps,
    | "onCompressDocumentsChange"
    | "onCompressRedo"
    | "onCompress"
    | "onCompressDownload"
    | "onDownload"
    | "onMergeError"
  > {
  origin: PdfCompressionOrigin;
}

export interface MergedCompressionSnapshot {
  pdf: ArrayBuffer;
  renderVersion: number;
  fileName: string;
  title: string;
  documents: PdfMergeDocument[];
  renderedDocuments: PdfMergeDocument[];
}

type Operation = {
  token: number;
  generation: number;
  epoch: number;
  kind: "compress" | "rotate";
};

type CompressState = {
  entries: CompressEntry[];
  uploads: { id: string; fileName: string }[];
  activeId: string;
  error: string | null;
  failures: Record<string, string>;
  operations: Record<string, Operation>;
};

const emptyState = (): CompressState => ({
  entries: [],
  uploads: [],
  activeId: "",
  error: null,
  failures: {},
  operations: {},
});

function withoutKey<T>(
  values: Record<string, T>,
  key: string
): Record<string, T> {
  const next = { ...values };
  delete next[key];
  return next;
}

/** Shared controller, instantiated independently for each compression origin. */
export function usePdfCompress(options: UsePdfCompressOptions) {
  const { origin } = options;
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const compressFileInputRef = useRef<HTMLInputElement>(null);
  const sequenceRef = useRef(0);
  const epochRef = useRef(0);
  const mountedRef = useRef(true);
  const [state, setState] = useState<CompressState>(emptyState);
  // The synchronous snapshot makes duplicate clicks and overlapping async edits
  // see the latest transaction, even before React commits a render.
  const stateRef = useRef(state);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      epochRef.current += 1;
    };
  }, []);

  const reportError = useCallback(
    (
      error: unknown,
      operation: PdfCompressionErrorContext["operation"],
      documentId?: string
    ) => {
      const failure = error instanceof Error ? error : new Error(String(error));
      if (mountedRef.current) {
        const current = stateRef.current;
        const next =
          documentId && current.entries.some((entry) => entry.id === documentId)
            ? {
                ...current,
                failures: {
                  ...current.failures,
                  [documentId]: failure.message,
                },
              }
            : { ...current, error: failure.message };
        stateRef.current = next;
        setState(() => next);
      }
      try {
        optionsRef.current.onMergeError?.(failure, {
          origin,
          operation,
          documentId,
        });
      } catch {
        // An error observer must not prevent the remaining download emissions.
      }
      return failure;
    },
    [origin]
  );

  const emit = useCallback(
    (callback: () => void, documentId?: string) => {
      try {
        callback();
      } catch (error) {
        reportError(error, "callback", documentId);
      }
    },
    [reportError]
  );

  const commit = useCallback(
    (update: (current: CompressState) => CompressState) => {
      if (!mountedRef.current) return;
      const current = stateRef.current;
      const next = update(current);
      stateRef.current = next;
      // The updater is pure: callback emissions and ref writes happen outside it.
      setState(() => next);
      if (next.entries !== current.entries) {
        emit(() =>
          optionsRef.current.onCompressDocumentsChange?.(
            toCompressDocuments(next.entries),
            { origin }
          )
        );
      }
    },
    [emit, origin]
  );

  const reset = useCallback(() => {
    epochRef.current += 1;
    commit((current) => ({
      ...emptyState(),
      entries: current.entries.length ? [] : current.entries,
    }));
  }, [commit]);

  const currentEntry = () =>
    stateRef.current.entries.find(
      (entry) => entry.id === stateRef.current.activeId
    );
  const isBusy = (id: string) => stateRef.current.operations[id] != null;

  const beginOperation = (entry: CompressEntry, kind: Operation["kind"]) => {
    const operation: Operation = {
      token: ++sequenceRef.current,
      generation: entry.generation,
      epoch: epochRef.current,
      kind,
    };
    commit((current) => ({
      ...current,
      operations: { ...current.operations, [entry.id]: operation },
      failures: withoutKey(current.failures, entry.id),
    }));
    return operation;
  };

  const isCurrent = (id: string, operation: Operation) =>
    mountedRef.current &&
    operation.epoch === epochRef.current &&
    stateRef.current.operations[id]?.token === operation.token &&
    stateRef.current.entries.some(
      (entry) => entry.id === id && entry.generation === operation.generation
    );

  const finishOperation = (id: string, operation: Operation) => {
    if (!isCurrent(id, operation)) return;
    commit((current) => ({
      ...current,
      operations: withoutKey(current.operations, id),
    }));
  };

  const failOperation = (id: string, operation: Operation, error: unknown) => {
    if (!isCurrent(id, operation)) return;
    reportError(error, operation.kind, id);
  };

  const addCompressFile = async (file: File) => {
    const epoch = epochRef.current;
    const generation = ++sequenceRef.current;
    const id = `compress-${generation}`;
    let reading = false;
    try {
      if (classifyUpload(file) !== "pdf") {
        throw new Error(
          `${file.name} is not a PDF. Only .pdf files can be uploaded.`
        );
      }
      if (file.size > COMPRESS_MAX_BYTES) {
        throw new Error(
          `${file.name} is ${formatByteSize(
            file.size
          )}. The maximum size is 40 MB.`
        );
      }
      reading = true;
      commit((current) => ({
        ...current,
        uploads: [...current.uploads, { id, fileName: file.name }],
        error: null,
      }));
      const pdf = await fileToPdfBuffer(file);
      if (
        !mountedRef.current || epoch !== epochRef.current ||
        !stateRef.current.uploads.some((upload) => upload.id === id)
      ) return;
      const entry: CompressEntry = {
        id,
        origin,
        generation,
        name: fileBaseName(file),
        fileName: file.name,
        file,
        pdf,
        originalPdf: pdf.slice(0),
        compressedPdf: null,
        pdfRevision: 0,
        title: "",
        compressionType: "original",
        customCompressMode: "",
        customCompressValue: null,
        result: null,
      };
      commit((current) => ({
        ...current,
        entries: [...current.entries, entry],
        uploads: current.uploads.filter((upload) => upload.id !== id),
        activeId: id,
        error: null,
      }));
    } catch (error) {
      if (!mountedRef.current || epoch !== epochRef.current) return;
      // A cancelled read may still settle; discard both its result and error.
      if (stateRef.current.uploads.some((upload) => upload.id === id)) {
        commit((current) => ({
          ...current,
          uploads: current.uploads.filter((upload) => upload.id !== id),
        }));
      } else if (reading) {
        return;
      }
      reportError(error, "upload", id);
    }
  };

  const cancelCompressUpload = (id: string) => {
    commit((current) => ({
      ...current,
      uploads: current.uploads.filter((upload) => upload.id !== id),
    }));
  };

  const loadMergedDocument = (snapshot: MergedCompressionSnapshot): boolean => {
    if (snapshot.pdf.byteLength > COMPRESS_MAX_BYTES) {
      const failure = new Error(
        `The merged document is ${formatByteSize(
          snapshot.pdf.byteLength
        )}. The maximum size is 40 MB.`
      );
      commit((current) => ({ ...current, error: failure.message }));
      reportError(failure, "initialize", MERGED_COMPRESS_ID);
      return false;
    }
    const existing = stateRef.current.entries.find(
      (entry) => entry.id === MERGED_COMPRESS_ID
    );
    const sameRender = existing?.mergedFromVersion === snapshot.renderVersion;
    const file = pdfBytesToFile(snapshot.pdf, snapshot.fileName);
    const entry: CompressEntry =
      sameRender && existing
        ? {
            ...existing,
            name: fileBaseName(file),
            fileName: file.name,
            file,
            title:
              existing.title === existing.initialMergedTitle
                ? snapshot.title
                : existing.title,
            initialMergedTitle: snapshot.title,
            mergeDocuments: snapshot.documents.map((document) => ({
              ...document,
            })),
            renderedMergeDocuments: snapshot.renderedDocuments.map(
              (document) => ({ ...document })
            ),
          }
        : {
            id: MERGED_COMPRESS_ID,
            origin,
            generation: ++sequenceRef.current,
            name: fileBaseName(file),
            fileName: file.name,
            file,
            pdf: snapshot.pdf.slice(0),
            originalPdf: snapshot.pdf.slice(0),
            compressedPdf: null,
            pdfRevision: (existing?.pdfRevision ?? -1) + 1,
            title: snapshot.title,
            initialMergedTitle: snapshot.title,
            compressionType: "original",
            customCompressMode: "",
            customCompressValue: null,
            result: null,
            mergedFromVersion: snapshot.renderVersion,
            mergeDocuments: snapshot.documents.map((document) => ({
              ...document,
            })),
            renderedMergeDocuments: snapshot.renderedDocuments.map(
              (document) => ({ ...document })
            ),
          };
    commit((current) => ({
      ...current,
      entries: [entry],
      activeId: entry.id,
      error: null,
      failures: sameRender ? current.failures : {},
      operations: sameRender ? current.operations : {},
    }));
    return true;
  };

  const setActiveCompressId = (activeId: string) => {
    commit((current) => ({ ...current, activeId }));
  };

  const removeCompressDocument = (id: string) => {
    commit((current) => {
      const entries = current.entries.filter((entry) => entry.id !== id);
      return {
        ...current,
        entries,
        activeId:
          current.activeId === id
            ? entries[entries.length - 1]?.id ?? ""
            : current.activeId,
        failures: withoutKey(current.failures, id),
        operations: withoutKey(current.operations, id),
      };
    });
  };

  const updateCompressEntry = (
    id: string,
    patch: Partial<
      Pick<
        CompressEntry,
        | "title"
        | "compressionType"
        | "customCompressMode"
        | "customCompressValue"
      >
    >
  ) => {
    if (isBusy(id)) return;
    commit((current) => ({
      ...current,
      entries: current.entries.map((entry) =>
        entry.id === id ? { ...entry, ...patch } : entry
      ),
    }));
  };

  const handleCompressRedo = () => {
    const entry = currentEntry();
    if (!entry || isBusy(entry.id)) return;
    const redone: CompressEntry = {
      ...entry,
      generation: ++sequenceRef.current,
      pdf: entry.originalPdf.slice(0),
      pdfRevision: entry.pdfRevision + 1,
      compressedPdf: null,
      result: null,
      compressionType: "original",
      customCompressMode: "",
      customCompressValue: null,
    };
    commit((current) => ({
      ...current,
      entries: current.entries.map((item) =>
        item.id === entry.id ? redone : item
      ),
      failures: withoutKey(current.failures, entry.id),
    }));
    const document = toCompressDocuments([redone])[0];
    if (document)
      emit(() => optionsRef.current.onCompressRedo?.(document), entry.id);
  };

  const handleCompress = async () => {
    const entry = currentEntry();
    if (!entry || isBusy(entry.id) || !canCompressEntry(entry)) return;
    const bytes = entry.pdf.slice(0);
    const operation = beginOperation(entry, "compress");
    try {
      const response = await optionsRef.current.onCompress?.({
        ...toCompressDocuments([entry])[0]!,
        compressionType: entry.compressionType,
        sizeBytes: bytes.byteLength,
        sourceFile: entry.file,
        file: new Blob([new Uint8Array(bytes)], { type: "application/pdf" }),
      });
      if (!response || !isCurrent(entry.id, operation)) return;
      const pdf = await pdfBytesFromCompressResponse(response);
      if (!isCurrent(entry.id, operation)) return;
      commit((current) => ({
        ...current,
        entries: current.entries.map((item) =>
          item.id === entry.id
            ? {
                ...item,
                pdf,
                pdfRevision: item.pdfRevision + 1,
                compressedPdf: pdf.slice(0),
                result: {
                  compressedFileSize: response.compressed_file_size,
                  compressionPercent: response.compression_percent,
                  compressionTimeSeconds: response.compression_time_seconds,
                  targetMet: response.target_met,
                },
              }
            : item
        ),
      }));
    } catch (error) {
      failOperation(entry.id, operation, error);
    } finally {
      finishOperation(entry.id, operation);
    }
  };

  const emitMergedDownload = (
    output: Omit<MergedCompressionSnapshot, "renderVersion">,
    outputBlob?: Blob
  ) => {
    if (origin !== "merge") return;
    const pdf = outputBlob ? output.pdf : output.pdf.slice(0);
    const blob =
      outputBlob ??
      new Blob([new Uint8Array(pdf)], { type: "application/pdf" });
    const title = output.title.trim();
    emit(
      () =>
        optionsRef.current.onDownload?.({
          pdf,
          blob,
          fileName: output.fileName,
          ...(title ? { mergedTitle: title } : {}),
          documents: output.documents.map((document) => ({ ...document })),
          renderedDocuments: output.renderedDocuments.map((document) => ({
            ...document,
          })),
        }),
      MERGED_COMPRESS_ID
    );
  };

  const handleCompressDownload = () => {
    const entry = currentEntry();
    if (!entry || isBusy(entry.id) || !canDownloadEntry(entry)) return;
    const pdf = entry.pdf.slice(0);
    const blob = new Blob([new Uint8Array(pdf)], { type: "application/pdf" });
    const title = entry.title.trim();
    const fileName = documentFileName(title || entry.fileName);
    const document = toCompressDocuments([entry])[0];
    if (!document) return;
    const payload: PdfCompressDownload = {
      ...document,
      pdf,
      blob,
      downloadFileName: fileName,
      ...(origin === "merge"
        ? {
            documents: (entry.mergeDocuments ?? []).map((document) => ({
              ...document,
            })),
            renderedDocuments: (entry.renderedMergeDocuments ?? []).map(
              (document) => ({ ...document })
            ),
          }
        : {}),
    };
    try {
      downloadSource({ buffer: pdf, name: fileName }, fileName);
    } catch (error) {
      reportError(error, "download", entry.id);
    }
    emit(() => optionsRef.current.onCompressDownload?.(payload), entry.id);
    if (origin === "merge") {
      emitMergedDownload(
        {
          pdf,
          fileName,
          title,
          documents: entry.mergeDocuments ?? [],
          renderedDocuments: entry.renderedMergeDocuments ?? [],
        },
        blob
      );
    }
  };

  const rotateCompressDocument = async (id: string, degrees: number) => {
    const entry = stateRef.current.entries.find((item) => item.id === id);
    if (!entry || isBusy(id)) return;
    const operation = beginOperation(entry, "rotate");
    try {
      const pdf = await rotatePdfPages(entry.pdf, [
        { startPage: 1, endPage: Number.MAX_SAFE_INTEGER, rotation: degrees },
      ]);
      if (!isCurrent(id, operation)) return;
      commit((current) => ({
        ...current,
        entries: current.entries.map((item) =>
          item.id === id
            ? { ...item, pdf, pdfRevision: item.pdfRevision + 1 }
            : item
        ),
      }));
    } catch (error) {
      failOperation(id, operation, error);
    } finally {
      finishOperation(id, operation);
    }
  };

  const activeCompressEntry = state.entries.find(
    (entry) => entry.id === state.activeId
  );
  const compressingIds = new Set(
    Object.keys(state.operations).filter(
      (id) => state.operations[id]?.kind === "compress"
    )
  );
  const busyIds = new Set(Object.keys(state.operations));

  return {
    compressFileInputRef,
    compressEntries: state.entries,
    compressUploads: state.uploads,
    cancelCompressUpload,
    compressError: state.error,
    compressingIds,
    busyIds,
    activeCompressId: state.activeId,
    setActiveCompressId,
    activeCompressEntry,
    activeIsCompressing: activeCompressEntry
      ? compressingIds.has(activeCompressEntry.id)
      : false,
    activeIsBusy: activeCompressEntry
      ? busyIds.has(activeCompressEntry.id)
      : false,
    activeCompressError: activeCompressEntry
      ? state.failures[activeCompressEntry.id]
      : undefined,
    addCompressFile,
    loadMergedDocument,
    reset,
    removeCompressDocument,
    updateCompressEntry,
    handleCompressRedo,
    handleCompress,
    handleCompressDownload,
    rotateCompressDocument,
  };
}
